import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import VideoEditingService, { LibraryClipLabel } from 'services/VideoEditingService';
import BrollFilterBar, { humanize } from 'pages/BrollLibrary/BrollFilterBar';
import { useBrollSearch } from 'pages/BrollLibrary/useBrollSearch';
import { defaultBrollName, formatDuration } from '../format';

const videoEditingService = new VideoEditingService();

const PAGE_SIZES = [10, 25, 50];

/**
 * Search the b-roll library from inside a project and pull clips into it.
 *
 * Deliberately the same search as the library page — same hook, same filter bar — so a clip you
 * can find in one place can be found the same way in the other. Only the per-result action
 * differs: "Add" instead of Edit/Remove.
 *
 * Opens pre-filtered to the project's own product, since a project about mattress toppers
 * almost always wants topper footage; it's a starting point, not a restriction.
 */
export default function BrollPickerDialog({
  productCategory, alreadyAdded, onAdd, onClose,
}: {
  // The project's product, used to pre-select the filter.
  productCategory?: string | null;
  // Label ids already in the project, so they read as added rather than offering a duplicate.
  alreadyAdded: Set<string>;
  onAdd: (label: LibraryClipLabel) => Promise<void>;
  onClose: () => void;
}) {
  const search = useBrollSearch({ initialProductCategory: productCategory || null, pageSize: 10 });
  const { labels, total, loading, page, setPage, pageSize, setPageSize, categoryName } = search;
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pastedId, setPastedId] = useState('');
  const [idBusy, setIdBusy] = useState(false);

  async function add(label: LibraryClipLabel) {
    setAddingId(label.labelId);
    setError(null);
    try {
      await onAdd(label);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not add that clip');
    } finally {
      setAddingId(null);
    }
  }

  // Add by id, for a clip whose id was copied from the library page. It resolves to the same
  // record the search returns and goes through the same add, so nothing about the clip depends
  // on which way it was found.
  async function addById() {
    const id = pastedId.trim();
    if (!id) return;
    setIdBusy(true);
    setError(null);
    try {
      if (alreadyAdded.has(id)) { setError('That clip is already in this project.'); return; }
      const resp = await videoEditingService.resolveClipLabels([id]);
      const label = resp?.labels?.[0];
      if (!label) { setError(`No b-roll clip with the ID “${id}”.`); return; }
      await onAdd(label);
      setPastedId('');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not add that clip');
    } finally {
      setIdBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '86vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        Add From Library
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Clips you add show up on the B-Roll tab, ready to drag onto the timeline.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            sx={{ width: 340 }}
            label="B-Roll ID"
            placeholder="Paste an ID copied from the b-roll library"
            value={pastedId}
            onChange={e => setPastedId(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addById(); } }}
          />
          <Button
            variant="outlined"
            disabled={!pastedId.trim() || idBusy}
            startIcon={idBusy ? <CircularProgress size={14} color="inherit" /> : <AddIcon fontSize="small" />}
            onClick={() => void addById()}
          >
            Add
          </Button>
        </Box>
        <Box>
          <BrollFilterBar search={search} compact />
        </Box>

        {/* No product gate. Someone pulling b-roll into an edit is usually after a shot rather
            than a product ("hands", "morning light"), and being made to name a product first
            meant that search could not be run at all. */}
        {loading && !labels.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : !labels.length ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No clips match those filters.</Typography>
          </Paper>
        ) : (
          // Paging keeps the current results on screen and dims them, rather than emptying the
          // grid: a page change is fast enough that a blank flash reads as breakage, and a bar
          // plus the dim is what says "working" while the next page arrives.
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <LinearProgress
                aria-label="Loading clips"
                sx={{ position: 'absolute', top: -6, left: 0, right: 0, borderRadius: 1 }}
              />
            )}
            <Box sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' },
              opacity: loading ? 0.45 : 1,
              transition: 'opacity 120ms ease',
              // Not clickable while superseded — Add would otherwise fire on a card that is
              // about to be replaced by the next page's.
              pointerEvents: loading ? 'none' : 'auto',
            }}>
            {labels.map(label => {
              const added = alreadyAdded.has(label.labelId);
              return (
                <Paper key={label.labelId} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{
                    position: 'relative', backgroundColor: '#0F172A', aspectRatio: '9 / 16', maxHeight: 200,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {label.posterUrl ? (
                      <img
                        src={label.posterUrl}
                        alt={label.description || label.fileName || 'clip preview'}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>no preview yet</Typography>
                    )}
                    {label.inSec != null && label.outSec != null && (
                      <Chip
                        size="small"
                        label={`${formatDuration(label.inSec)}–${formatDuration(label.outSec)}`}
                        sx={{ position: 'absolute', top: 6, left: 6, height: 20, fontSize: 11, backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.3 }}>
                      {label.name || defaultBrollName(label)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(label.categories || []).map(slug => (
                        <Chip key={slug} size="small" label={categoryName(slug)} sx={{ height: 16, fontSize: 9, backgroundColor: '#FEF3C7', color: '#92400E' }} />
                      ))}
                      {label.clipSubject.slice(0, 3).map(v => (
                        <Chip key={v} size="small" label={humanize(v)} sx={{ height: 16, fontSize: 9, backgroundColor: '#EEF2FF', color: '#4F46E5' }} />
                      ))}
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      size="small"
                      fullWidth
                      variant={added ? 'text' : 'contained'}
                      disabled={added || addingId === label.labelId}
                      startIcon={addingId === label.labelId
                        ? <CircularProgress size={12} color="inherit" />
                        : added ? <CheckIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                      onClick={() => add(label)}
                    >
                      {added ? 'Added' : 'Add'}
                    </Button>
                  </Box>
                </Paper>
              );
            })}
            </Box>
          </Box>
        )}

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={e => setPageSize(Number(e.target.value))}
            rowsPerPageOptions={PAGE_SIZES}
            labelRowsPerPage="Clips per page"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );
}
